/**
 * Multi-path OR: the same logical attribute can be reached from several filter
 * entry points (a direct field, rollups, and the same across datasets), each a
 * distinct filterId. `getFilterAttributeKey` returns a canonical identity for an
 * attribute so those entry points can be (a) auto-expanded together on the FE
 * and (b) OR'd together on the backend (which just groups by the stamped
 * `orGroupKey` — it does not recompute this).
 *
 * Two filters with the same key target the same underlying thing:
 *   - tags:  the tag collection (source.tagType) — direct tag fields AND
 *            tag-terminal rollups both carry source.tagType
 *   - value paths: the field path, normalised of its trailing ".value" so a
 *            direct `main.gender` field and a rollup terminating on
 *            `main.gender.value` resolve to the same key
 *
 * Returns null for filters with no groupable attribute (date/number ranges,
 * anything unrecognised) → the caller leaves them ungrouped (AND, as before).
 */

const stripValueSuffix = (p) =>
  typeof p === "string" && p.endsWith(".value") ? p.slice(0, -6) : p;

export const getFilterAttributeKey = (filter) => {
  if (!filter) return null;
  const source = filter.source || {};
  const target = filter.target || {};
  const scope = target.isAnno ? "anno" : "doc";

  // Tag attributes — direct tag fields and tag-terminal rollups both set source.tagType.
  if (source.tagType) return `${scope}:tag:${source.tagType}`;

  // Direct value-path field (dropdown / radio / checkbox).
  if (target.filterType === "valuePathType" && target.valuePath) {
    return `${scope}:vp:${stripValueSuffix(target.valuePath)}`;
  }

  // Documents filter. It has no target.valuePath — the field its document ids
  // match is resolved per query on the backend (_id on the contributions
  // collections, meta.documentId on annotations/chunks), so there is no single
  // path to key on. Keyed by identity instead, on the same `doc:vp:_id` key it
  // had when its target was valuePathType/_id, so grouping behaviour is
  // unchanged across the retarget (in practice it is the only entry point to
  // this attribute, so it stays a singleton either way).
  if (target.filterType === "documentsType") return `${scope}:vp:_id`;

  // Hybrid rollup terminating on a value path (rollupPath ends in "main.*").
  if (
    target.filterType === "nestedRollupTagType" &&
    Array.isArray(target.rollupPath) &&
    target.rollupPath.length
  ) {
    const terminal = target.rollupPath[target.rollupPath.length - 1];
    if (typeof terminal === "string" && terminal.startsWith("main.")) {
      return `${scope}:vp:${stripValueSuffix(terminal)}`;
    }
    return null; // a tag-terminal rollup would have hit source.tagType above
  }

  // Value path rolled up from a connected collection. Key on the terminal field
  // ALONE (`vp:<field>`) — exactly like the direct valuePathType and the
  // value-terminal nestedRollupTagType above — NOT namespaced by the rollup
  // collection. A direct filter on a field and a rollup that lands on that same
  // field are two entry points to the SAME attribute, so they must share a key
  // and OR together. (Namespacing it as `rvp:<collection>:<field>` gave it a
  // distinct key and silently broke that grouping.)
  if (target.filterType === "rollupValuePathType" && target.valuePathInRolledUpCollection) {
    return `${scope}:vp:${stripValueSuffix(target.valuePathInRolledUpCollection)}`;
  }

  // Range types (date/number) and anything else are not auto-OR'd.
  return null;
};

/**
 * The other filterIds in `flatConfig` that reach the SAME attribute as
 * `changedFilterId` (same getFilterAttributeKey), excluding itself. [] when the
 * filter has no groupable attribute or no siblings.
 *
 * @param {Array} flatConfig  result of flattenFilterConfig(filterConfig)
 */
export const getSiblingFilterIds = (flatConfig, changedFilterId) => {
  const changed = flatConfig.find((f) => f.filterId === changedFilterId);
  const key = getFilterAttributeKey(changed);
  if (!key) return [];
  return flatConfig
    .filter((f) => f.filterId !== changedFilterId && getFilterAttributeKey(f) === key)
    .map((f) => f.filterId);
};
